import React, { useState } from 'react'
import { TextField as FoundationsTextField, Typography as FoundationsTypography } from 'foundations-library/components'
import { createStyles, makeStyles } from '@material-ui/core'
import * as GTM from '../../../util/GTM'

const useStyles = makeStyles(theme =>
    createStyles({
        couponForm: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'left',
            margin: '10px 0'
        },
        iconButton: {
            width: '22px',
            height: '22px',
            marginRight: '8.5px',
            padding: '0px',
            '& svg': {
                width: '12px',
                height: '12px'
            }
        },
        addIcon: {
        },
        cancelIcon: {
            '& svg': {
                width: '16px',
                height: '16px'
            }
        },
        textField: {
            '& .text-field': {
                border: 0
            }
        },
        footer: {
            marginTop: '17px',
            marginBottom: '0px'
        },
        couponDescription: {
            marginTop: '20px',
            marginBottom: '0px',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '10px',
            lineHeight: '12px',
            letterSpacing: '0.4px',
            textTransform: 'uppercase'
        }
    })
)

interface CouponProps {
    setCouponCode: (text : string) => void,
    couponCodeApplied: string | null,
    couponCodeAttempted: string | null,
    couponDesc: string | null,
    disabled?: boolean
}

/**
 * Coupon entry/display widget, as part of the OrderSummary
 */
const Coupon = ({ setCouponCode, couponCodeApplied, couponCodeAttempted, couponDesc, disabled } : CouponProps) : JSX.Element => {
    const [text, setText] = useState<string>('')

    const classes = useStyles()

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        event.stopPropagation()
        couponCodeApplied ? clearCoupon() : setCouponCode(text)
    }

    const clearCoupon = () => {
        setCouponCode('')
        GTM.couponCleared()
    }

    let textFieldValue = text
    if (couponCodeApplied) {
        // Note: the backend returns "coupon_code": "error" rather than than actual code attempted,
        // so display the code that was attempted instead of "error"
        textFieldValue = couponCodeApplied === 'error' ? couponCodeAttempted : couponCodeApplied
    }

    const addCoupon = () => {
        if (disabled) return
        const couponForm = document.querySelector('.coupon-form') as HTMLFormElement
        if (couponForm) {
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
            couponForm.dispatchEvent(submitEvent)
        }
    }

    return (
        <form className={`${classes.couponForm} coupon-form`} onSubmit={onSubmit}>
            <FoundationsTextField {...{ 'data-testid': 'coupon-textfield' }} placeholder='Enter coupon code' value={textFieldValue} onChange={value => setText(value.toUpperCase())}
                className={classes.textField}
                disabled={Boolean(couponCodeApplied) || disabled}
                keepIconActive={true}
                fullWidth trailingIcon={!couponCodeApplied ? 'add-button' : 'remove-button'} onClickTrailingIcon={addCoupon}
            />

            {couponCodeApplied &&
                <>
                    {couponCodeApplied === 'error'
                        ? (
                            <FoundationsTypography variant="helperText" align="left" color="destructive-500" className={classes.footer}>
                                That coupon code is invalid.
                            </FoundationsTypography>
                        )
                        : (
                            <FoundationsTypography variant="helperText" align="left" color="secondary-500" className={classes.footer}>
                                Your coupon has been applied.
                            </FoundationsTypography>
                        )}
                </>
            }

            {(couponDesc && couponCodeApplied !== 'error')
                ? (
                    <FoundationsTypography variant="captionHeavy" align="left" color="neutral-900">
                        {couponDesc}
                    </FoundationsTypography>
                )
                : null}
        </form>
    )
}

export default Coupon
